import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const BusinessSettingsContext = createContext();

// Default settings as fallback
const defaultSettings = {
  business_name: 'Boyken Trailer Rentals',
  tagline: 'Your Adventure Starts Here',
  logo_url: null,
  contact_email: 'boykentrailerrentals@gmail.com',
  contact_phone: '(555) 123-4567',
  contact_address: 'Cross Plains, Indiana',
  primary_color: '#FF6B35',
  secondary_color: '#1e293b',
  facebook_url: null,
  instagram_url: null,
  tiktok_url: null,
  youtube_url: null,
  twitter_url: null,
  linkedin_url: null,
  business_hours: null,
  // Pricing
  deposit_percentage: 25,
  default_cleaning_fee: 150,
  tax_rate: 7,
  // Delivery
  delivery_price_per_mile: 2.50,
  delivery_minimum_fee: 50,
  delivery_max_distance: 150,
  delivery_base_address: '',
  // Booking rules
  advance_booking_days: 30,
  min_booking_nights: 1,
  min_advance_booking_days: 2, // submission gate; replaces the old 48-hour hardcode
  // Default times + timezone (admin-display use)
  timezone: 'America/Indiana/Indianapolis',
  default_pickup_time: '14:00',
  default_return_time: '11:00',
  // SEO fields
  meta_description: null,
  og_image_url: null,
  favicon_url: null,
  industry_types: ['rv', 'travel_trailer'],
  cancellation_policy: [
    { min_days: 31, max_days: null, refund_percent: 100, label: 'More than 30 days before departure' },
    { min_days: 8, max_days: 30, refund_percent: 50, label: '8 to 30 days before departure' },
    { min_days: 0, max_days: 7, refund_percent: 0, label: '7 days or fewer before departure' }
  ],
  faqs: [
    { question: 'Can I see what the rental agreement looks like?', answer: 'Yes — you can review the full rental agreement here: <a href="/rental-agreement">View sample rental agreement</a>. The actual agreement is personalized with your booking details (trailer, dates, total) and presented for your signature during checkout.' },
    { question: 'What do I need to rent a trailer?', answer: "You'll need a valid driver's license, proof of insurance, and a vehicle capable of towing. You must be at least 25 years old. A credit card is required for the security deposit." },
    { question: 'Do you offer delivery?', answer: 'Yes! We offer delivery and setup within our service area for an additional fee. Delivery costs are based on distance from our location. Contact us for a quote.' },
    { question: 'What if something breaks during my rental?', answer: "For minor issues, give us a call and we'll troubleshoot. For emergencies, we offer 24/7 phone support. Damage beyond normal wear may be deducted from your security deposit." },
    { question: 'How early should I book?', answer: 'We recommend booking 2-4 weeks in advance, especially for weekends and holidays. Popular dates can book up quickly during peak season (May-September).' },
    { question: 'What is included in the rental?', answer: 'All rentals include basic equipment: hitch, safety chains, breakaway cable, and a walkthrough before departure. Kitchen items, linens, and other add-ons may be available for additional fees.' },
    { question: 'Can I take the trailer out of state?', answer: 'Yes, travel within the continental US is generally permitted. Let us know your destination during booking so we can ensure you have appropriate documentation.' },
    { question: 'What time is pickup and return?', answer: 'Standard pickup is 2:00 PM and return is by 11:00 AM. We can sometimes accommodate different times based on availability - just ask when booking.' },
    { question: 'Is there a minimum rental period?', answer: 'Our minimum rental is typically 2 nights, though this may vary by season. Weekly and monthly rentals are available at discounted rates.' },
    { question: 'Do you offer discounts?', answer: 'Yes! We offer discounts for weekly and monthly rentals, military and first responders, and repeat customers. Contact us to learn about current promotions.' }
  ],
  terms_of_service: '',
  privacy_policy: '',
  cancellation_page_content: ''
};

// Apply colors to CSS custom properties
const applyColors = (primaryColor, secondaryColor) => {
  const root = document.documentElement;
  root.style.setProperty('--primary-color', primaryColor);
  root.style.setProperty('--secondary-color', secondaryColor);

  // Create a lighter version of primary for hover states
  root.style.setProperty('--primary-color-light', adjustColor(primaryColor, 20));
  root.style.setProperty('--primary-color-dark', adjustColor(primaryColor, -20));

  // RGB triplet variant so CSS can compose translucent variants via
  // `rgba(var(--primary-color-rgb), 0.18)` — used for focus-ring halos
  // and similar fades that have no static fallback when the brand color
  // changes per-tenant.
  root.style.setProperty('--primary-color-rgb', hexToRgbTriplet(primaryColor));
};

// Helper to lighten/darken a hex color
const adjustColor = (hex, percent) => {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, Math.max(0, (num >> 16) + amt));
  const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amt));
  const B = Math.min(255, Math.max(0, (num & 0x0000FF) + amt));
  return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
};

// "#FF6B35" → "255, 107, 53". Returns a default if the input is malformed
// so a bad DB value can't blow up downstream rgba() composition.
const hexToRgbTriplet = (hex) => {
  const cleaned = String(hex || '').replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(cleaned)) return '255, 107, 53';
  const num = parseInt(cleaned, 16);
  return `${(num >> 16) & 255}, ${(num >> 8) & 255}, ${num & 255}`;
};

export function BusinessSettingsProvider({ children }) {
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  // Apply colors whenever settings change
  useEffect(() => {
    applyColors(settings.primary_color, settings.secondary_color);
  }, [settings.primary_color, settings.secondary_color]);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/public`);
      const data = await response.json();

      if (data.success && data.settings) {
        // Merge with defaults to ensure all fields exist
        const newSettings = {
          ...defaultSettings,
          ...data.settings
        };
        setSettings(newSettings);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching business settings:', error);
      // Keep using default settings on error
    } finally {
      setLoading(false);
    }
  };

  return (
    <BusinessSettingsContext.Provider value={{ settings, loading, refreshSettings: fetchSettings }}>
      {children}
    </BusinessSettingsContext.Provider>
  );
}

export function useBusinessSettings() {
  const context = useContext(BusinessSettingsContext);
  if (!context) {
    throw new Error('useBusinessSettings must be used within a BusinessSettingsProvider');
  }
  return context;
}

export default BusinessSettingsContext;
